import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import find from 'lodash.find';
import scrollToComponent from 'react-scroll-to-component';

import CircularProgress from '@material-ui/core/CircularProgress';

import FormattedMessageWrappedInSpan from './FormattedMessageWrappedInSpan';

import GenerateVideoMutation from '../../mutations/GenerateVideoMutation';
import GenerateZipMutation from '../../mutations/GenerateZipMutation';

import { ORIGINAL, SOCIAL_MEDIA } from '../../utils/variables';
import environment from '../../environment';


const DownloadDropdown = (props) => {
  // map props to useful state object
  const initialValues = props.values.map(value => ({
    id: value,
    isPending: false,
    url: null,
    failed: false,
  }));

  const { fullWidth, downloadType, eventSgId } = props;

  const dropdownRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    if (expanded) {
      if (window.innerWidth < 768) {
        scrollToComponent(dropdownRef.current, { align: 'top' });
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [expanded]);

  const handleClickOutside = (event) => {
    if (dropdownRef && !dropdownRef.current.contains(event.target)) {
      if (expanded) {
        setExpanded(false);
      }
    }
  }

  const handleExpandMenu = (newValue) => {
    setExpanded(newValue);
  }

  const _updateState = (id, key, newValue) => {
    let stateCopy = [...values];
    const obj = find(stateCopy, v => v.id === id);
    obj[key] = newValue;
    if (key === 'url') {
      // If the URL is set, the request was successful
      obj.failed = false;
    }
    setValues(stateCopy);
  }

  const _download = (url) => {
    window.location.href = url;
  }
  
  const _renderDropdownMainLabel = (downloadType) => {
    switch (downloadType) {
      case 'zip':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.zip"
            defaultMessage="Download photos as ZIP"
          />
        );
      case 'video':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.video"
            defaultMessage="Download video"
          />
        );
      case 'certificate':
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.certificate"
            defaultMessage="Download certificate"
          />
        );
      default:
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.mainLabel.default"
            defaultMessage="Download"
          />
        );
    }
  }
  
  const _renderDropdownItemLabel = (value) => {
    if (downloadType === 'zip') {
      if (value === ORIGINAL) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.original"
            defaultMessage="High resolution"
          />
        );
      } else if (value === SOCIAL_MEDIA) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.socialmedia"
            defaultMessage="Social media resolution"
          />
        );
      } else {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.requestLabel.zip.startNumber"
            defaultMessage="Start number {startNumber}"
            values={{ startNumber: value }}
          />
        );
      }
    } else if (downloadType === 'video') {
      return (
        <FormattedMessageWrappedInSpan
          id="downloadDropdown.requestLabel.video"
          defaultMessage="Start number {startNumber}"
          values={{ startNumber: value }}
        />
      );
    }
  }
  
  const _renderDropdownItemFailedLabel = (value) => {
    if (downloadType === 'zip') {
      if (value === ORIGINAL) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.original"
            defaultMessage="Retry (high resolution)"
          />
        );
      } else if (value === SOCIAL_MEDIA) {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.socialmedia"
            defaultMessage="Retry (social media resolution)"
          />
        );
      } else {
        return (
          <FormattedMessageWrappedInSpan
            id="downloadDropdown.failedLabel.zip.startNumber"
            defaultMessage="Retry (start number {startNumber})"
            values={{ startNumber: value }}
          />
        );
      }
    } else if (downloadType === 'video') {
      return (
        <FormattedMessageWrappedInSpan
          id="downloadDropdown.failedLabel.video"
          defaultMessage="Retry (start number {startNumber})"
          values={{ startNumber: value }}
        />
      );
    }
  }

  const handleButtonClicked = (value) => {
    if (value.url) {
      _download(value.url);
    } else {
      _generateDownloadLink(downloadType, value.id);
    }
  }

  const _generateDownloadLink = (downloadType, id) => {
    _updateState(id, 'isPending', true);
    switch (downloadType) {
      case 'zip':
        _generateZipDownloadLink(id);
        return;
      case 'video':
        _generateVideoDownloadLink(id);
        return;
      default:
        return;
    }
  }

  const _generateZipDownloadLink = (value) => {
    GenerateZipMutation.commit(
      environment,
      eventSgId,
      value,
      (errorMsg, url) => {
        if (!errorMsg) {
          _updateState(value, 'url', url);
          _download(url);
        } else {
          _updateState(value, 'failed', true);
        }
        _updateState(value, 'isPending', false);
      }
    );
  }

  const _generateVideoDownloadLink = (startNumber) => {
    GenerateVideoMutation.commit(
      environment,
      eventSgId,
      startNumber,
      (error, url) => {
        if (!error) {
          _updateState(startNumber, 'url', url);
          _download(url);
        } else {
          _updateState(startNumber, 'failed', true);
        }
        _updateState(startNumber, 'isPending', false);
      }
    );
  }
  
  return (
    <div
      ref={dropdownRef}
      className={classNames('download-component-container', {
        'full-width': fullWidth,
      })}
    >
      <div
        className={classNames('download-component-main', {
          expanded: expanded,
          'zip-download': downloadType === 'zip',
        })}
        onClick={() => handleExpandMenu(!expanded)}
      >
        {_renderDropdownMainLabel(downloadType)}
        <div className="download-dropdown-expand-icon-container">
          <span
            className={classNames('download-dropdown-expand-icon', {
              expanded: expanded,
            })}
          />
        </div>
      </div>
      <div className={classNames('download-dropdown-content', { expanded: expanded })}>
        {values.map((value, index) => {
          return (
            <button
              key={index}
              className={classNames('download-dropdown-item', {
                disabled: value.isPending,
              })}
              disabled={value.isPending}
              onClick={() => handleButtonClicked(value)}
            >
              {!value.failed && _renderDropdownItemLabel(value.id)}
              {value.failed && _renderDropdownItemFailedLabel(value.id)}
              {value.isPending && (
                <div className="download-dropdown-expand-icon-container">
                  <CircularProgress
                    classes={{ circle: 'circular-progress-circle-copy-color-medium' }}
                    size={20}
                    thickness={3}
                  />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

DownloadDropdown.defaultProps = {
  downloadType: 'none',
};

DownloadDropdown.propTypes = {
  eventSgId: PropTypes.number,
  fullWidth: PropTypes.bool,
  downloadType: PropTypes.oneOf(['zip', 'video', 'certificate']).isRequired,
  values: PropTypes.array,
};

export default DownloadDropdown;
